import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, FormGroup, FormText, Input, Label, Row } from 'reactstrap';
import { OptionFileInput } from '../../../shared/options';
import { CurrentUser } from '../../../shared/user/base';
import { UpdateUserPayload } from '../../../shared/user/update';
import {
  CountrySelector,
  PageHeader,
  PhoneInput,
  RegionSelector,
  UserStateComponent,
} from '../../components';
import { ActionType, store } from '../../services';
import { useTranslation } from '../../translations';
import { Fetcher, useFetcher, useForm, useInputState } from '../../utils';
import { ProfilePictureEditor } from './profilePictureEditor';

export const EditPersonalComponent: FC = () => {
  const fetch = useFetcher(async () => {
    return await api.getActiveUser();
  });

  if (!fetch.data) {
    return <Fetcher result={fetch} />;
  }

  return <EditPersonalComponentInner refresh={fetch.refresh} user={fetch.data} />;
};

interface EditPersonalComponentProps {
  readonly user: CurrentUser;
  readonly refresh: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const EditPersonalComponentInner: FC<EditPersonalComponentProps> = ({
  user: inputUser,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [firstName, setFirstName] = useInputState(inputUser.firstName ?? '');
  const [lastName, setLastName] = useInputState(inputUser.lastName ?? '');
  const [preferredName, setPreferredName] = useInputState(inputUser.preferredName, true);
  const [bornAt, setBornAt] = useState(inputUser.bornAt);
  const [phone, setPhone] = useState(inputUser.phone);
  const [addressCity, setAddressCity] = useInputState(inputUser.addressCity);
  const [addressCountry, setAddressCountry] = useState(inputUser.addressCountry);
  const [addressLine1, setAddressLine1] = useInputState(inputUser.addressLine1);
  const [addressLine2, setAddressLine2] = useInputState(inputUser.addressLine2, true);
  const [addressState, setAddressState] = useState(inputUser.addressState);
  const [addressZipcode, setAddressZipcode] = useInputState(inputUser.addressZipcode);
  const [profilePicture, setProfilePicture] = useState<OptionFileInput | undefined | null>();

  const form = useForm(async () => {
    const user: UpdateUserPayload = {
      addressCity,
      addressCountry,
      addressLine1,
      addressLine2,
      addressState,
      addressZipcode,
      bornAt,
      firstName,
      lastName,
      phone,
      preferredName,
      profilePicture,
    };

    const { profilePictureUrl } = await api.updateUser(inputUser.id, user);
    toast.success(ts('your_personal_information_has_been'));

    store.dispatch({
      type: ActionType.ProfileUpdate,
      user: { ...inputUser, user, profilePictureUrl },
    });

    refresh();
  }, [
    addressCity,
    addressCountry,
    addressLine1,
    addressLine2,
    addressState,
    addressZipcode,
    bornAt,
    firstName,
    lastName,
    phone,
    preferredName,
    profilePicture,
    inputUser,
    ts,
    refresh,
  ]);

  const updateDoB = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    if (!Number.isNaN(date.getTime())) {
      setBornAt(date);
    }
  }, []);

  const warnDob = useCallback(() => toast.warn(ts('if_you_need_to_change')), [ts]);

  return (
    <UserStateComponent>
      <PageHeader>{ts('common.edit_personal_details')}</PageHeader>
      <Row className="justify-content-center">
        <Col lg={6} xs={12}>
          <Card>
            <CardBody id="personalForm">
              <form onSubmit={form.onSubmit}>
                <Row className="justify-content-center">
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="firstName">{ts('common.first_name')}</Label>
                      <Input
                        disabled={inputUser.identityVerified}
                        id="firstName"
                        onChange={setFirstName}
                        value={firstName}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="lastName">{ts('common.last_name')}</Label>
                      <Input
                        disabled={inputUser.identityVerified}
                        id="lastName"
                        onChange={setLastName}
                        value={lastName}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <FormText color="muted">{ts('these_must_be_your_legal')}</FormText>
                </FormGroup>
                <Row className="justify-content-center clearfix-after">
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="preferredName">{ts('common.preferred_name')}</Label>
                      <Input
                        id="preferredName"
                        onChange={setPreferredName}
                        value={preferredName ?? ''}
                      />
                      <FormText color="muted">
                        {ts('common.optional')} {ts('common.if_you_have_a_name')}
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="bornAt">{ts('common.date_of_birth')}</Label>
                      <Input
                        defaultValue={bornAt?.toJSON().slice(0, 10) ?? ''}
                        id="bornAt"
                        onChange={updateDoB}
                        onClick={bornAt ? warnDob : undefined}
                        readOnly={!!inputUser.bornAt}
                        type="date"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <hr />
                <Row className="justify-content-center">
                  <Col className="text-center" lg={6} xs={12}>
                    <ProfilePictureEditor
                      ignoreGravatar={false}
                      inputUser={inputUser}
                      onChange={setProfilePicture}
                      title={ts('profile_picture')}
                    />
                  </Col>
                </Row>
                <hr />
                <FormGroup>
                  <Label for="addressLine1">{ts('common.address_line_1')}</Label>
                  <Input
                    autoComplete="address-line1"
                    id="addressLine1"
                    onChange={setAddressLine1}
                    placeholder={`${ts('common.example')}: 1 Magical Lane`}
                    value={addressLine1}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="addressLine2">{ts('common.address_line_2_optional')}</Label>
                  <Input
                    autoComplete="address-line2"
                    id="addressLine2"
                    onChange={setAddressLine2}
                    placeholder={`${ts('common.example')}: Apt 2`}
                    value={addressLine2 ?? ''}
                  />
                  <FormText color="muted">{ts('common.optional')}</FormText>
                </FormGroup>
                <Row className="justify-content-center">
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="addressCity">{ts('common.city')}</Label>
                      <Input
                        autoComplete="address-level2"
                        id="addressCity"
                        onChange={setAddressCity}
                        placeholder={`${ts('common.example')}: Reno`}
                        value={addressCity}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="addressZipcode">{ts('common.zip_postal_code')}</Label>
                      <Input
                        autoComplete="postal-code"
                        id="addressZipcode"
                        onChange={setAddressZipcode}
                        placeholder={`${ts('common.example')}: 89501`}
                        value={addressZipcode}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="addressCountry">{ts('common.country')}</Label>
                      <CountrySelector
                        autoComplete="country-name"
                        className="form-control"
                        id="addressCountry"
                        onChange={setAddressCountry}
                        value={addressCountry}
                        // Note: React types not compatible with country selector
                        valueType="short"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={6} xs={12}>
                    <FormGroup>
                      <Label for="addressZipcode">{ts('common.state_province')}</Label>
                      <RegionSelector
                        autoComplete="address-level1"
                        className="form-control"
                        country={addressCountry}
                        countryValueType="short"
                        data-coerce={false}
                        disableWhenEmpty
                        id="addressState"
                        onChange={setAddressState}
                        value={addressState}
                        valueType="short"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="phone">{ts('common.phone_number')}</Label>
                  <PhoneInput
                    name="phone"
                    onChange={setPhone as Dispatch<SetStateAction<string | null>>}
                    value={phone}
                  />
                </FormGroup>
                <hr />
                <Row className="justify-content-center">
                  <Col className="mt-10" lg={6} xs={12}>
                    <Button
                      block
                      color="primary"
                      disabled={form.saving}
                      id="updatePersonalDetails"
                      type="submit"
                    >
                      {ts('common.update')}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </UserStateComponent>
  );
};
